import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, AlertCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { fetchPortfolio, savePortfolioToSupabase, isAuthenticated, getAuthState } from '@/utils/kiteConnect';
import { KiteHolding, KitePosition } from '@/utils/kiteTypes';

const REFRESH_INTERVAL = 60000; // 1 minute

export default function KitePortfolio() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const userId = user?.id || '';

  // Query for portfolio data
  const { 
    data: portfolioData, 
    isLoading: isLoadingPortfolio,
    isError: isErrorPortfolio 
  } = useQuery({
    queryKey: ['kitePortfolio', userId],
    queryFn: async () => {
      if (!userId || !isAuthenticated()) return null;
      
      // Try to fetch from database first
      const { data: holdings, error: holdingsError } = await supabase
        .from('kite_holdings')
        .select('*')
        .eq('user_id', userId)
        .order('tradingsymbol');
        
      const { data: positions, error: positionsError } = await supabase
        .from('kite_positions')
        .select('*')
        .eq('user_id', userId)
        .order('tradingsymbol');
        
      if (holdingsError || positionsError) {
        // If database fetch fails, try to fetch from Kite API
        const portfolioResult = await fetchPortfolio();
        if (portfolioResult.success) {
          // Save to database
          await savePortfolioToSupabase(userId, portfolioResult.holdings, portfolioResult.positions);
          return portfolioResult;
        }
        throw holdingsError || positionsError;
      }
      
      return {
        holdings: holdings || [],
        positions: { net: positions || [], day: [] }
      };
    },
    enabled: !!userId && isAuthenticated(),
    refetchInterval: REFRESH_INTERVAL,
  });

  // Function to refresh portfolio data
  const refreshPortfolio = async () => {
    try {
      setIsRefreshing(true);
      
      if (!isAuthenticated()) {
        toast.error('Not authenticated. Please connect to Kite first.');
        return;
      }
      
      // Fetch portfolio data from Kite API
      const portfolioResult = await fetchPortfolio();
      
      if (!portfolioResult.success) {
        toast.error(portfolioResult.error || 'Failed to fetch portfolio data from Kite API');
        return;
      }
      
      // Save to database
      const saveResult = await savePortfolioToSupabase(userId, portfolioResult.holdings, portfolioResult.positions);
      
      if (!saveResult.success) {
        toast.error('Failed to save portfolio data to database');
        return;
      }
      
      // Refresh the React Query cache
      await queryClient.invalidateQueries({ queryKey: ['kitePortfolio', userId] });
      
      toast.success('Portfolio data refreshed successfully');
    } catch (error: any) {
      toast.error(`Failed to refresh portfolio: ${error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Calculate portfolio summary
  const getPortfolioSummary = () => {
    if (!portfolioData || !portfolioData.holdings) return null;
    
    const holdings = portfolioData.holdings;
    const positions = portfolioData.positions?.net || [];
    
    let totalValue = 0;
    let totalInvestment = 0;
    let totalPnl = 0;
    let totalDayChange = 0;
    
    holdings.forEach((holding: KiteHolding) => {
      const currentValue = holding.quantity * holding.last_price;
      const investmentValue = holding.quantity * holding.average_price;
      
      totalValue += currentValue;
      totalInvestment += investmentValue;
      totalPnl += holding.pnl;
      totalDayChange += holding.day_change;
    });
    
    positions.forEach((position: KitePosition) => {
      totalValue += position.value;
      totalPnl += position.pnl;
    });
    
    const totalPnlPercentage = totalInvestment > 0 ? (totalPnl / totalInvestment) * 100 : 0;
    const dayChangePercentage = totalValue > 0 ? (totalDayChange / totalValue) * 100 : 0;
    
    return {
      totalValue,
      totalInvestment,
      totalPnl,
      totalPnlPercentage,
      totalDayChange,
      dayChangePercentage,
      holdingsCount: holdings.length,
      positionsCount: positions.length
    };
  };

  const summary = getPortfolioSummary();

  if (!isAuthenticated()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Portfolio Data</CardTitle>
          <CardDescription>
            Connect your Zerodha account to view portfolio data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Please connect your Zerodha account to view portfolio data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoadingPortfolio) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Portfolio Data</CardTitle>
          <CardDescription>Loading your portfolio...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <RefreshCw className="h-12 w-12 mx-auto text-gray-400 mb-4 animate-spin" />
            <p className="text-gray-600">Loading portfolio data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isErrorPortfolio) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Portfolio Data</CardTitle>
          <CardDescription>Error loading portfolio data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 mx-auto text-red-400 mb-4" />
            <p className="text-red-600 mb-4">Failed to load portfolio data</p>
            <Button onClick={refreshPortfolio} disabled={isRefreshing}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Portfolio Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Value</p>
                  <p className="text-2xl font-bold">₹{summary.totalValue.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Invested</p>
                  <p className="text-lg">₹{summary.totalInvestment.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total P&L</p>
                  <p className={`text-2xl font-bold ${summary.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalPnl >= 0 ? '+' : ''}₹{summary.totalPnl.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Return</p>
                  <p className={`text-lg ${summary.totalPnlPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalPnlPercentage >= 0 ? '+' : ''}{summary.totalPnlPercentage.toFixed(2)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Day Change</p>
                  <p className={`text-2xl font-bold ${summary.totalDayChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalDayChange >= 0 ? '+' : ''}₹{summary.totalDayChange.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className={`flex items-center ${summary.dayChangePercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.dayChangePercentage >= 0 ? 
                      <ArrowUpRight className="h-4 w-4 mr-1" /> : 
                      <ArrowDownRight className="h-4 w-4 mr-1" />
                    }
                    <span>{Math.abs(summary.dayChangePercentage).toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Holdings</p>
                  <p className="text-2xl font-bold">{summary.holdingsCount}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Positions</p>
                  <p className="text-lg">{summary.positionsCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Portfolio Data */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Portfolio Data</CardTitle>
              <CardDescription>
                Your holdings and positions from Zerodha
              </CardDescription>
            </div>
            <Button 
              onClick={refreshPortfolio} 
              disabled={isRefreshing}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="holdings" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="holdings">
                Holdings ({portfolioData?.holdings?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="positions">
                Positions ({portfolioData?.positions?.net?.length || 0})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="holdings" className="space-y-4">
              {portfolioData?.holdings && portfolioData.holdings.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Avg Price</TableHead>
                      <TableHead>Last Price</TableHead>
                      <TableHead>P&L</TableHead>
                      <TableHead>Day Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portfolioData.holdings.map((holding: KiteHolding, index: number) => (
                      <TableRow key={`${holding.instrument_token}_${index}`}>
                        <TableCell className="font-medium">
                          {holding.tradingsymbol}
                          <div className="text-sm text-gray-500">{holding.exchange}</div>
                        </TableCell>
                        <TableCell>{holding.quantity}</TableCell>
                        <TableCell>₹{holding.average_price?.toFixed(2)}</TableCell>
                        <TableCell>₹{holding.last_price?.toFixed(2)}</TableCell>
                        <TableCell className={holding.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {holding.pnl >= 0 ? '+' : ''}₹{holding.pnl?.toFixed(2)}
                        </TableCell>
                        <TableCell className={holding.day_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {holding.day_change >= 0 ? '+' : ''}₹{holding.day_change?.toFixed(2)}
                          <div className="text-sm">
                            ({holding.day_change_percentage >= 0 ? '+' : ''}{holding.day_change_percentage?.toFixed(2)}%)
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No holdings found</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="positions" className="space-y-4">
              {portfolioData?.positions?.net && portfolioData.positions.net.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Avg Price</TableHead>
                      <TableHead>Last Price</TableHead>
                      <TableHead>P&L</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portfolioData.positions.net.map((position: KitePosition, index: number) => (
                      <TableRow key={`${position.instrument_token}_${index}`}>
                        <TableCell className="font-medium">
                          {position.tradingsymbol}
                          <div className="text-sm text-gray-500">{position.exchange}</div>
                        </TableCell>
                        <TableCell>{position.quantity}</TableCell>
                        <TableCell>₹{position.average_price?.toFixed(2)}</TableCell>
                        <TableCell>₹{position.last_price?.toFixed(2)}</TableCell>
                        <TableCell className={position.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {position.pnl >= 0 ? '+' : ''}₹{position.pnl?.toFixed(2)}
                        </TableCell>
                        <TableCell>₹{position.value?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No positions found</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}